import React, { useEffect, useState } from "react";

import { getUserRoles, saveUserRoles } from "../services/accessService";
import type { ApprovalType, Props, Role } from "../types/access";

const ManageRoles: React.FC<Props> = ({ open, onClose, members, roles, createdBy }) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [roleMap, setRoleMap] = useState<Record<string, { role: Role; approval: ApprovalType }>>(
    {}
  );

  /* ✅ Prefill from DB whenever user changes */
  useEffect(() => {
    if (!selectedUser) {
      setRoleMap({});
      return;
    }

    getUserRoles(selectedUser).then((res) => {
      const map: typeof roleMap = {};
      res.data.forEach((r: any) => {
        const role = roles.find((x) => x.role_code === r.role_code);
        if (role) {
          map[role.role_code] = {
            role,
            approval: r.approval_type,
          };
        }
      });

      setRoleMap(map);
    });
  }, [selectedUser, roles]);

  const handleApply = async () => {
    await saveUserRoles({
      user_principal_name: selectedUser,
      created_by: createdBy,
      roles: Object.values(roleMap)
        .filter((r) => r.approval !== "") // Only include roles where approval_type is not 'Not Assigned' (empty string)
        .map((r) => ({
          role_code: r.role.role_code,
          approval_type: r.approval,
        })),
    });

    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Manage Roles</h3>

        {/* User */}
        <label className="block text-sm font-medium mb-1">User</label>
        <select
          className="w-full border p-2 rounded mb-6"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <option value="">Select user</option>
          {members.map((m) => (
            <option key={m.id} value={m.userPrincipalName}>
              {m.userPrincipalName || m.displayName}
            </option>
          ))}
        </select>

        {/* Roles */}
        <div className="border rounded divide-y">
          {roles.map((role) => (
            <div
              key={role.role_code}
              className="flex justify-between items-center px-3 py-2 hover:bg-sky-50"
            >
              <span className="font-medium text-sm">{role.role_code}</span>

              <select
                className="border px-2 py-1 rounded text-sm"
                value={roleMap[role.role_code]?.approval ?? ""}
                onChange={(e) => {
                  const value = e.target.value as ApprovalType | "";
                  setRoleMap((prev) => {
                    const next = { ...prev };
                    if (!value) {
                      delete next[role.role_code];
                    } else {
                      next[role.role_code] = { role, approval: value };
                    }
                    return next;
                  });
                }}
              >
                <option value="">Not Assigned</option>
                <option value="reviewer">Reviewer</option>
                <option value="approver">Approver</option>
              </select>
            </div>
          ))}
        </div>

        <button
          className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded"
          onClick={handleApply}
          disabled={!selectedUser}
        >
          Apply
        </button>

        <div className="mt-3 text-right">
          <button className="text-sm text-gray-500" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageRoles;
